import React, { useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import ScheduleForm from "../../WorkOrder/Forms/ScheduleForm";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { useMutation } from "@tanstack/react-query";
import { useSectionChanges } from "../../../hooks/useSectionChanges";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import { pick } from "lodash";
import { useResetValidationOnClose } from "../../../hooks/useResetValidationOnClose";

const VALIDATED_FIELDS = ["orderStartDate", "orderStartTime"];

const EDITABLE_SCHEDULE_FIELDS = [
  "scheduleType",
  "orderStartDate",
  "orderStartTime",
  "orderEndDate",
  "orderEndTime",
  "timeZone",
];

const normalizeSchedule = (schedule) => pick(schedule ?? {}, EDITABLE_SCHEDULE_FIELDS);

const EditScheduleModal = ({ open, close, workOrderData }) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const { updateWorkOrder } = useUpdateWorkOrder();

  const schedule = useSelector((state) => state.workOrder.scheduling.schedule);
  const { hasChanges, onHydrate } = useSectionChanges(schedule, normalizeSchedule);
  useResetValidationOnClose(VALIDATED_FIELDS);

  const {
    isPending: isUpdatingLoading,
    error: isUpdatingError,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: (data) => {},
  });

  // Mirrors ScheduleForm's own gate. The timezone comes from the work order's
  // location, so without it the location is unknown and the schedule fields are
  // not even rendered — there is nothing to save. Read straight from the store
  // rather than through the form ref, so it re-evaluates on every keystroke.
  const isLocationKnown = !!schedule?.timeZone;
  const hasDateAndTime = !!schedule?.orderStartDate && !!schedule?.orderStartTime;
  const canSave = isLocationKnown && hasDateAndTime;

  const handleSubmit = async () => {
    // isValid() returns { valid, errors } — the bare object is always truthy,
    // so testing it directly let every incomplete schedule through. Normalise
    // it the same way the create wizard's Next button does.
    const { valid, errors } = normalizeValidationResult(formRef.current?.isValid?.());

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) {
        dispatch(showToast({ message, type: "error" }));
      }
      focusFirstInvalidField();
      return;
    }

    try {
      await updateWorkOrderApiMutation({
        id: workOrderData.id,
        ...schedule,
      });
      updateWorkOrder();

      dispatch(
        showToast({
          message: "Schedule updated successfully!",
          type: "success",
        }),
      );

      close(false);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          message: "Failed to update schedule.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="sm:-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-3 sm:py-7 px-4 sm:px-12 w-full sm:w-[800px] lg:w-[1000px] xl:w-[1400px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Schedule</h6>

        <div className="mb-2 mt-3">
          <ScheduleForm
            ref={formRef}
            isEditMode={true}
            workOrderData={workOrderData}
            onHydrate={onHydrate}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          disabled={!canSave || isUpdatingLoading || !hasChanges}
          onClick={handleSubmit}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditScheduleModal;
